import React, { useState } from 'react';
import type { PageDetailOut } from '../../types/catalog';
import type { McqInteraction } from '../../types/page';
import { isMcqInteraction } from '../../types/page';
import { ConfettiBurst } from '../components/ConfettiBurst';
import { Button } from '../../../../components/ui/Button';

interface McqChoiceV1Props {
  page: PageDetailOut;
  onComplete?: () => void;
}

export const McqChoiceV1: React.FC<McqChoiceV1Props> = ({ page, onComplete }) => {
  const interactions = page.page_json_v1?.interactions ?? [];
  const mcq = interactions.find(isMcqInteraction) as McqInteraction | undefined;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [completed, setCompleted] = useState(false);

  if (!mcq) {
    return (
      <div className="p-6 text-gray-500 dark:text-gray-400">
        No MCQ interaction found.
      </div>
    );
  }

  const options = mcq.options ?? [];
  const correctId = mcq.answer_option_id;
  const isCorrect = selectedId === correctId;

  const handleSubmit = () => {
    if (selectedId === null) return;
    setSubmitted(true);
    if (selectedId === correctId) {
      setShowConfetti(true);
      setCompleted(true);
      onComplete?.();
    }
  };

  const getOptionStyle = (optId: string) => {
    if (!submitted) {
      return selectedId === optId
        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600';
    }
    if (optId === correctId) return 'border-green-500 bg-green-50 dark:bg-green-900/30';
    if (optId === selectedId && optId !== correctId) return 'border-red-500 bg-red-50 dark:bg-red-900/30';
    return 'border-gray-200 dark:border-gray-700 opacity-60';
  };

  return (
    <div className="space-y-6 p-6 overflow-auto">
      <ConfettiBurst trigger={showConfetti} duration={1500} />
      <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
        {page.page_title || 'Multiple Choice'}
      </h1>
      <p className="text-gray-700 dark:text-gray-300">{mcq.prompt}</p>
      <div className="space-y-3">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => !submitted && setSelectedId(opt.id)}
            disabled={submitted}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-colors ${getOptionStyle(opt.id)} ${
              submitted ? 'cursor-default' : 'cursor-pointer'
            }`}
          >
            <span className="text-lg font-medium text-gray-900 dark:text-white">
              {opt.text ?? opt.number ?? opt.id}
            </span>
          </button>
        ))}
      </div>
      <div className="flex gap-3">
        <Button
          onClick={handleSubmit}
          disabled={selectedId === null || submitted}
        >
          Submit
        </Button>
        {submitted && !isCorrect && (
          <span className="text-red-600 dark:text-red-400 font-medium">Wrong. Try again.</span>
        )}
        {submitted && isCorrect && (
          <span className="text-green-600 dark:text-green-400 font-medium">Correct!</span>
        )}
      </div>
    </div>
  );
};
